import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { normalizeRole, deriveSectorFromRole, ViewAsUser } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { useData } from "../hooks/useData";
import { useRBAC } from "../hooks/useRBAC";
import { usePresence } from "../hooks/usePresence";
import CurrencyToggle from "./CurrencyToggle";
import { roleConfig, canAccessPageForUser } from "../config/roleConfig";
import {
  SIDEBAR_ITEMS,
  isOwner,
  FLAT_LAYOUT_ORDER,
  SECTIONED_LAYOUT,
  CATEGORIES,
  CATEGORY_ORDER,
} from "../config/sidebarItems";
import type { CategoryKey } from "../config/sidebarItems";
import { searchNav } from "../utils/navSearch";
// useTheme import removed — Altara is light-mode-only, no theme toggle needed
import type { UserRole } from "../types/core";

// =============================================================================
// SIDEBAR NAVIGATION — Config-Driven, Category Nav + Synonym Search
// =============================================================================
// PURPOSE: Left navigation with org selector, RBAC-gated synonym search,
// role-conditional links, view-as dropdown.
//
// ARCHITECTURE:
// - Page access is driven by roleConfig[role].pages (via canAccessPage).
// - Item metadata (label, icon, CATEGORY, search ALIASES) lives in
//   config/sidebarItems.ts — editing that file updates both this component AND
//   the dev SidebarViewerPage.
// - Layout is chosen per-role:
//     • exec / manager (sectioned)  → collapsible CATEGORY accordion
//     • closer/setter/dialer, client → flat list, SUB-GROUPED by category label
//     • collapsed rail (w-14)        → flat icon-only list (no headers/search)
// - SEARCH is available to every role. Results are derived from the SAME
//   RBAC + devMode filtered set the nav renders, so a rep can never search up a
//   page they can't open (RBAC is inherited from canAccessPage — see visiblePaths).
// =============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// Navigation Icons — imported from shared module (also used by MobileBottomNav)
// ─────────────────────────────────────────────────────────────────────────────
import { NavIcons as Icons } from "./ui/Icons";

const CATEGORY_META = Object.fromEntries(CATEGORIES.map((c) => [c.key, c])) as Record<
  CategoryKey,
  (typeof CATEGORIES)[number]
>;

// ─────────────────────────────────────────────────────────────────────────────
// NavItem Components
// ─────────────────────────────────────────────────────────────────────────────

const NavItem: React.FC<{
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  collapsed?: boolean;
  indented?: boolean;
  tag?: string; // optional right-aligned category tag (used in search results)
  highlighted?: boolean; // keyboard-selected search result (Enter jumps here)
}> = ({ to, icon, label, onClick, collapsed, indented, tag, highlighted }) => (
  <NavLink
    to={to}
    onClick={onClick}
    title={collapsed ? label : undefined}
    role={highlighted !== undefined ? "option" : undefined}
    aria-selected={highlighted !== undefined ? highlighted : undefined}
    className={({ isActive }) =>
      `sidebar-nav-item token-calendar flex items-center ${collapsed ? "justify-center" : "gap-2"} ${
        indented ? "pl-3.5 pr-2" : "px-2"
      } py-1.5 rounded-lg transition-all duration-150 ${
        isActive
          ? "bg-blue-500/15 text-blue-400"
          : highlighted
            ? "bg-blue-500/[0.08] text-glass ring-1 ring-inset ring-blue-500/50"
            : "text-glass-secondary hover:bg-black/[0.04] hover:text-glass"
      }`
    }
  >
    <span className="flex-shrink-0 text-current opacity-70">{icon}</span>
    {!collapsed && <span className="truncate flex-1">{label}</span>}
    {!collapsed && tag && (
      <span className="token-9 uppercase text-glass-muted tracking-wide flex-shrink-0">{tag}</span>
    )}
  </NavLink>
);

// Sub-group label used in the flat (rep/client) layout — smaller + tighter than a
// full section header because it sits inside a single flat list, not a new section.
const SubGroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-2 pt-2.5 pb-1 token-9 text-glass-muted uppercase tracking-wide">
    {children}
  </div>
);

// Collapsible category header row (exec/manager sectioned layout).
const CategoryHeader: React.FC<{
  label: string;
  icon: React.ReactNode;
  count: number;
  open: boolean;
  onToggle: () => void;
}> = ({ label, icon, count, open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className={`sidebar-nav-item token-calendar w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 ${
      open
        ? "text-glass bg-black/[0.03]"
        : "text-glass-secondary hover:bg-black/[0.04] hover:text-glass"
    }`}
  >
    <span className="flex-shrink-0 text-current opacity-70">{icon}</span>
    <span className="truncate flex-1 text-left font-medium">{label}</span>
    <span className="token-9 text-glass-muted tabular-nums flex-shrink-0">{count}</span>
    <span
      className={`flex-shrink-0 text-glass-muted transition-transform duration-150 ${
        open ? "rotate-180" : ""
      }`}
    >
      <Icons.ChevronDown />
    </span>
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// Exec / Manager Category Nav — HOVER-OUT FLYOUT (Mako, Jul 9 — replaces the
// rejected inline click-expand accordion). The rail shows ONLY the ~6 category
// rows; hovering one floats its child pages out in a Portal'd panel to the RIGHT
// of the rail (mock #1, "two-tier flyout rail").
//   • Portal → document.body: the sidebar <aside> is `liquid-glass` +
//     `backdrop-filter`, a stacking/paint context that would trap an in-tree
//     panel poking over page content (see /css). Portal escapes it.
//   • `detail-overlay` (NOT `liquid-glass`) is the sanctioned overlay surface,
//     and its 0.94 opacity keeps the panel readable over page content.
//   • Enter is OPACITY-ONLY (no transform slide like the raw mock): `detail-
//     overlay` has backdrop-filter, and a transform transition on it repaints
//     the blur → ~0.4s text blur (see /css "never add transform to glass hover").
//   • Desktop only. Mobile/narrow keeps the inline accordion (hover ≠ touch).
// ─────────────────────────────────────────────────────────────────────────────

type FlyoutCategory = { cat: CategoryKey; paths: string[]; meta: (typeof CATEGORIES)[number] };

const FLYOUT_CLOSE_DELAY = 220; // ms — grace period before a panel closes on leave
const AIM_RECHECK = 200; // ms — re-check cadence while the cursor aims at the panel
const MOUSE_TRACK = 3; // recent cursor samples kept for the aim calculation

const slope = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  (b.y - a.y) / (b.x - a.x);

const ExecCategoryNav: React.FC<{
  categories: FlyoutCategory[];
  activeCategory?: CategoryKey;
  renderNavItem: (
    path: string,
    opts?: { indented?: boolean; tag?: string; highlighted?: boolean }
  ) => React.ReactNode;
}> = ({ categories, activeCategory, renderNavItem }) => {
  const [shownCat, setShownCat] = useState<CategoryKey | null>(null);
  const [pinnedCat, setPinnedCat] = useState<CategoryKey | null>(null);
  const rowRefs = useRef<Map<CategoryKey, HTMLButtonElement | null>>(new Map());
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);
  const aimTimer = useRef<number | null>(null);
  const mouseLocs = useRef<{ x: number; y: number }[]>([]);
  const [pos, setPos] = useState({ top: 0, left: 0, notch: 20, opacity: 0 });

  const clearHide = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const clearAim = () => {
    if (aimTimer.current) {
      window.clearTimeout(aimTimer.current);
      aimTimer.current = null;
    }
  };
  // On leave, revert to the pinned category (or close if nothing pinned) after a
  // grace delay so a diagonal cursor move row → panel doesn't dismiss it.
  const scheduleHide = () => {
    clearHide();
    hideTimer.current = window.setTimeout(() => setShownCat(pinnedCat), FLYOUT_CLOSE_DELAY);
  };

  // "Safe triangle" (menu-aim): is the cursor DIVING into the open panel? If so, a
  // sibling row the cursor grazes en route must NOT steal the panel. Compare the
  // cursor's slope to the panel's near-edge corners now vs. one sample ago — if the
  // aim cone is narrowing toward the panel, the user is heading for it. An idle
  // cursor (they've settled on a row) is NOT diving, so the graze can activate.
  const aimingAtPanel = () => {
    const panel = panelRef.current;
    if (!shownCat || !panel) return false;
    const locs = mouseLocs.current;
    const loc = locs[locs.length - 1];
    const prev = locs[locs.length - 2] || loc;
    if (!loc || loc === prev) return false;
    const r = panel.getBoundingClientRect();
    const BUF = 40; // pad the panel's top/bottom so the triangle is forgiving
    const top = { x: r.left, y: r.top - BUF };
    const bot = { x: r.left, y: r.bottom + BUF };
    return slope(loc, top) < slope(prev, top) && slope(loc, bot) > slope(prev, bot);
  };

  // Open `cat` — but if the cursor is diving toward the current panel, defer and
  // re-check, so grazing this row mid-dive doesn't yank the panel away.
  const possiblyActivate = (cat: CategoryKey) => {
    clearHide();
    clearAim();
    if (cat === shownCat) return;
    if (aimingAtPanel()) {
      // Snapshot the latest cursor sample. If it's unchanged at re-check time, the
      // cursor has SETTLED on this row (stopped diving) → activate it. Otherwise
      // the dive continues → keep deferring. `mouseLocs` pushes a fresh object per
      // move, so identity comparison = "did the cursor move since we scheduled?".
      const settledAt = mouseLocs.current[mouseLocs.current.length - 1];
      aimTimer.current = window.setTimeout(() => {
        const now = mouseLocs.current[mouseLocs.current.length - 1];
        if (now === settledAt) {
          clearAim();
          setShownCat(cat);
        } else {
          possiblyActivate(cat);
        }
      }, AIM_RECHECK);
    } else {
      setShownCat(cat);
    }
  };
  // Keyboard focus / touch — no aim heuristic, just open it.
  const openImmediate = (cat: CategoryKey) => {
    clearHide();
    clearAim();
    setShownCat(cat);
  };
  // Click pins (keyboard + touch fallback); click the pinned row again to close.
  const togglePin = (cat: CategoryKey) => {
    clearHide();
    clearAim();
    if (pinnedCat === cat) {
      setPinnedCat(null);
      setShownCat(null);
    } else {
      setPinnedCat(cat);
      setShownCat(cat);
    }
  };
  const closeAll = () => {
    clearHide();
    clearAim();
    setPinnedCat(null);
    setShownCat(null);
  };

  useEffect(
    () => () => {
      clearHide();
      clearAim();
    },
    []
  );

  // Track the cursor globally WHILE a panel is open — the row→panel transit crosses
  // page content (the panel is Portal'd off the rail), so a container-scoped
  // listener would miss it. Feeds aimingAtPanel().
  useEffect(() => {
    if (!shownCat) return;
    const onMove = (e: MouseEvent) => {
      const locs = mouseLocs.current;
      locs.push({ x: e.clientX, y: e.clientY });
      if (locs.length > MOUSE_TRACK) locs.shift();
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [shownCat]);

  // Position the flyout next to its row (fixed/viewport coords — Portal is on body).
  useLayoutEffect(() => {
    if (!shownCat) return;
    const compute = () => {
      const row = rowRefs.current.get(shownCat);
      const panel = panelRef.current;
      if (!row || !panel) return;
      const r = row.getBoundingClientRect();
      const h = panel.offsetHeight;
      const GAP = 10;
      let top = r.top - 8;
      const maxTop = window.innerHeight - h - 12;
      if (top > maxTop) top = maxTop;
      if (top < 12) top = 12;
      let notch = r.top - top + r.height / 2 - 5.5; // keep the pointer on the row
      if (notch < 12) notch = 12;
      if (notch > h - 20) notch = h - 20;
      setPos({ top, left: Math.round(r.right + GAP), notch: Math.round(notch), opacity: 1 });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [shownCat]);

  const current = shownCat ? categories.find((c) => c.cat === shownCat) : undefined;

  return (
    <div className="pt-1 space-y-0.5">
      {categories.map(({ cat, paths, meta }) => {
        const Icon = Icons[meta.iconKey];
        const isOpen = shownCat === cat;
        const isActiveParent = activeCategory === cat;
        return (
          <button
            key={cat}
            type="button"
            ref={(el) => {
              rowRefs.current.set(cat, el);
            }}
            onMouseEnter={() => possiblyActivate(cat)}
            onMouseLeave={scheduleHide}
            onFocus={() => openImmediate(cat)}
            onClick={() => togglePin(cat)}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            className={`sidebar-nav-item token-calendar relative w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 ${
              isActiveParent
                ? "text-blue-400 bg-blue-500/10"
                : isOpen
                  ? "text-glass bg-black/[0.04]"
                  : "text-glass-secondary hover:bg-black/[0.04] hover:text-glass"
            }`}
          >
            {isActiveParent && (
              <span
                className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-blue-400"
                aria-hidden="true"
              />
            )}
            <span
              className={`flex-shrink-0 ${isActiveParent ? "text-blue-400" : "text-current opacity-70"}`}
            >
              <Icon />
            </span>
            <span className="truncate flex-1 text-left font-medium">{meta.label}</span>
            <span className="token-9 text-glass-muted tabular-nums flex-shrink-0">
              {paths.length}
            </span>
            <span className="flex-shrink-0 text-glass-muted opacity-70">
              <Icons.ChevronRight />
            </span>
          </button>
        );
      })}

      {current &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label={current.meta.label}
            onMouseEnter={() => {
              clearHide();
              clearAim();
            }}
            onMouseLeave={scheduleHide}
            style={
              {
                position: "fixed",
                top: pos.top,
                left: pos.left,
                opacity: pos.opacity,
                zIndex: 9990,
                "--notch": `${pos.notch}px`,
              } as React.CSSProperties
            }
            className="sidebar-flyout detail-overlay border border-black/[0.1] rounded-xl shadow-xl w-52 p-2 transition-opacity duration-150"
          >
            <div className="flex items-center justify-between px-2 pt-1 pb-1.5">
              <span className="token-9 uppercase text-glass-muted tracking-wide">
                {current.meta.label}
              </span>
              <span className="token-9 text-glass-muted tabular-nums">{current.paths.length}</span>
            </div>
            {/* Any leaf click bubbles here → close the flyout after navigating. */}
            <div className="space-y-0.5" onClick={closeAll}>
              {current.paths.map((path) => renderNavItem(path))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Org Flyout — side flyout on hover + click (same /triangle menu-aim as the
// category nav). One trigger, one panel that floats to the RIGHT; hovering the
// trigger opens it, clicking a row switches org. Because there's a single
// trigger (not a rail of rows that swap the panel), the safe-triangle lives on
// the HIDE path: while the cursor dives toward the open panel, the grace-close
// is deferred so the diagonal trigger→panel transit can't drop the menu.
// Desktop-only (hover); touch/collapsed fall back to the native <select>.
// ─────────────────────────────────────────────────────────────────────────────

const OrgFlyout: React.FC<{
  organizations: { id: string; name: string }[];
  currentId: string | null;
  currentName: string | null;
  onSelect: (id: string) => void;
}> = ({ organizations, currentId, currentName, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);
  const aimTimer = useRef<number | null>(null);
  const pinnedRef = useRef(false); // timer callbacks read this, not the stale closure
  const mouseLocs = useRef<{ x: number; y: number }[]>([]);
  const [pos, setPos] = useState({ top: 0, left: 0, notch: 20, opacity: 0 });

  const clearHide = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const clearAim = () => {
    if (aimTimer.current) {
      window.clearTimeout(aimTimer.current);
      aimTimer.current = null;
    }
  };

  // Menu-aim "safe triangle": is the cursor DIVING toward the open panel? Compare
  // the cursor's slope to the panel's near-edge corners now vs. one sample ago —
  // a narrowing aim cone means the user is heading for it. Same heuristic as
  // ExecCategoryNav; here it defers the close rather than a sibling switch.
  const aimingAtPanel = () => {
    const panel = panelRef.current;
    if (!open || !panel) return false;
    const locs = mouseLocs.current;
    const loc = locs[locs.length - 1];
    const prev = locs[locs.length - 2] || loc;
    if (!loc || loc === prev) return false;
    const r = panel.getBoundingClientRect();
    const BUF = 40;
    const top = { x: r.left, y: r.top - BUF };
    const bot = { x: r.left, y: r.bottom + BUF };
    return slope(loc, top) < slope(prev, top) && slope(loc, bot) > slope(prev, bot);
  };

  // Close after a grace delay — unless pinned, or the cursor is still aiming into
  // the panel (then re-check every AIM_RECHECK until it SETTLES off-panel).
  const maybeHide = () => {
    if (pinnedRef.current) return;
    if (aimingAtPanel()) {
      const settledAt = mouseLocs.current[mouseLocs.current.length - 1];
      aimTimer.current = window.setTimeout(() => {
        const now = mouseLocs.current[mouseLocs.current.length - 1];
        if (now === settledAt) {
          clearAim();
          setOpen(false);
        } else {
          maybeHide(); // cursor still moving toward the panel → keep it alive
        }
      }, AIM_RECHECK);
    } else {
      setOpen(false);
    }
  };
  const scheduleHide = () => {
    clearHide();
    clearAim();
    hideTimer.current = window.setTimeout(maybeHide, FLYOUT_CLOSE_DELAY);
  };

  const openNow = () => {
    clearHide();
    clearAim();
    setOpen(true);
  };
  const togglePin = () => {
    clearHide();
    clearAim();
    const next = !pinned;
    setPinned(next);
    pinnedRef.current = next;
    setOpen(next);
  };
  const closeAll = () => {
    clearHide();
    clearAim();
    setPinned(false);
    pinnedRef.current = false;
    setOpen(false);
  };

  useEffect(
    () => () => {
      clearHide();
      clearAim();
    },
    []
  );

  // Track the cursor globally while the panel is open (it's Portal'd off the rail,
  // so a container-scoped listener would miss the transit). Feeds aimingAtPanel().
  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      const locs = mouseLocs.current;
      locs.push({ x: e.clientX, y: e.clientY });
      if (locs.length > MOUSE_TRACK) locs.shift();
    };
    const onKey = (e: KeyboardEvent) => {
      // Inlined (not closeAll) so the effect's only deps stay [open] — refs +
      // state setters are stable, so no exhaustive-deps churn.
      if (e.key === "Escape") {
        if (hideTimer.current) {
          window.clearTimeout(hideTimer.current);
          hideTimer.current = null;
        }
        if (aimTimer.current) {
          window.clearTimeout(aimTimer.current);
          aimTimer.current = null;
        }
        setPinned(false);
        pinnedRef.current = false;
        setOpen(false);
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Position the panel next to the trigger (fixed coords — Portal is on body).
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const r = trigger.getBoundingClientRect();
      const h = panel.offsetHeight;
      const GAP = 10;
      let top = r.top - 8;
      const maxTop = window.innerHeight - h - 12;
      if (top > maxTop) top = maxTop;
      if (top < 12) top = 12;
      let notch = r.top - top + r.height / 2 - 5.5;
      if (notch < 12) notch = 12;
      if (notch > h - 20) notch = h - 20;
      setPos({ top, left: Math.round(r.right + GAP), notch: Math.round(notch), opacity: 1 });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="org-flyout-trigger"
        onMouseEnter={openNow}
        onMouseLeave={scheduleHide}
        onFocus={openNow}
        onClick={togglePin}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`w-full flex items-center gap-2 bg-black/[0.03] border text-glass token-calendar rounded-lg py-1.5 pl-2 pr-2 font-medium cursor-pointer transition-colors ${
          open ? "border-blue-500 bg-black/[0.06]" : "border-black/[0.08] hover:bg-black/[0.06]"
        }`}
      >
        <span className="truncate flex-1 text-left">{currentName || "Select org"}</span>
        <span className="flex-shrink-0 text-glass-muted opacity-70">
          <Icons.ChevronRight />
        </span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label="Switch organization"
            onMouseEnter={() => {
              clearHide();
              clearAim();
            }}
            onMouseLeave={scheduleHide}
            style={
              {
                position: "fixed",
                top: pos.top,
                left: pos.left,
                opacity: pos.opacity,
                zIndex: 9990,
                "--notch": `${pos.notch}px`,
              } as React.CSSProperties
            }
            className="sidebar-flyout detail-overlay border border-black/[0.1] rounded-xl shadow-xl w-56 p-2 transition-opacity duration-150"
          >
            <div className="px-2 pt-1 pb-1.5">
              <span className="token-9 uppercase text-glass-muted tracking-wide">Organization</span>
            </div>
            <div className="space-y-0.5">
              {organizations.map((org) => {
                const isCurrent = org.id === currentId;
                return (
                  <button
                    key={org.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isCurrent}
                    onClick={() => {
                      onSelect(org.id);
                      closeAll();
                    }}
                    className={`token-calendar w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 ${
                      isCurrent
                        ? "text-blue-400 bg-blue-500/10"
                        : "text-glass-secondary hover:bg-black/[0.04] hover:text-glass"
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 w-4 ${isCurrent ? "text-blue-400" : "opacity-0"}`}
                      aria-hidden="true"
                    >
                      <Icons.Check />
                    </span>
                    <span className="truncate flex-1 text-left font-medium">{org.name}</span>
                  </button>
                );
              })}
              {organizations.length === 0 && (
                <div className="px-2 py-1.5 token-calendar text-glass-muted">No Orgs Found</div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// View-As Flyout — same hover-out /triangle treatment as OrgFlyout, for the
// exec-only impersonation picker. Single trigger; the safe-triangle lives on the
// HIDE path so the diagonal trigger→panel transit can't drop the menu. Items
// carry a role sublabel and the list scrolls (a big team has many members).
// Desktop-only (hover); touch/collapsed keep the native <select>.
// NOTE: deliberately mirrors OrgFlyout rather than sharing a component — the
// codebase already keeps ExecCategoryNav + OrgFlyout as separate copies of the
// safe-triangle machinery, and OrgFlyout is Mako-confirmed + 100+ specs deep. A
// future DRY pass could merge the two single-trigger flyouts (org + view-as)
// into one <SideFlyout>; kept separate here to avoid touching OrgFlyout.
// ─────────────────────────────────────────────────────────────────────────────

type ViewAsItem = { id: string; label: string; sublabel?: string };

const ViewAsFlyout: React.FC<{
  items: ViewAsItem[];
  currentId: string;
  triggerLabel: string;
  onSelect: (id: string) => void;
}> = ({ items, currentId, triggerLabel, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);
  const aimTimer = useRef<number | null>(null);
  const pinnedRef = useRef(false); // timer callbacks read this, not the stale closure
  const mouseLocs = useRef<{ x: number; y: number }[]>([]);
  const [pos, setPos] = useState({ top: 0, left: 0, notch: 20, opacity: 0 });

  const clearHide = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const clearAim = () => {
    if (aimTimer.current) {
      window.clearTimeout(aimTimer.current);
      aimTimer.current = null;
    }
  };

  // Menu-aim "safe triangle": is the cursor DIVING toward the open panel? Same
  // heuristic as ExecCategoryNav/OrgFlyout — here it defers the close.
  const aimingAtPanel = () => {
    const panel = panelRef.current;
    if (!open || !panel) return false;
    const locs = mouseLocs.current;
    const loc = locs[locs.length - 1];
    const prev = locs[locs.length - 2] || loc;
    if (!loc || loc === prev) return false;
    const r = panel.getBoundingClientRect();
    const BUF = 40;
    const top = { x: r.left, y: r.top - BUF };
    const bot = { x: r.left, y: r.bottom + BUF };
    return slope(loc, top) < slope(prev, top) && slope(loc, bot) > slope(prev, bot);
  };

  const maybeHide = () => {
    if (pinnedRef.current) return;
    if (aimingAtPanel()) {
      const settledAt = mouseLocs.current[mouseLocs.current.length - 1];
      aimTimer.current = window.setTimeout(() => {
        const now = mouseLocs.current[mouseLocs.current.length - 1];
        if (now === settledAt) {
          clearAim();
          setOpen(false);
        } else {
          maybeHide(); // cursor still moving toward the panel → keep it alive
        }
      }, AIM_RECHECK);
    } else {
      setOpen(false);
    }
  };
  const scheduleHide = () => {
    clearHide();
    clearAim();
    hideTimer.current = window.setTimeout(maybeHide, FLYOUT_CLOSE_DELAY);
  };

  const openNow = () => {
    clearHide();
    clearAim();
    setOpen(true);
  };
  const togglePin = () => {
    clearHide();
    clearAim();
    const next = !pinned;
    setPinned(next);
    pinnedRef.current = next;
    setOpen(next);
  };
  const closeAll = () => {
    clearHide();
    clearAim();
    setPinned(false);
    pinnedRef.current = false;
    setOpen(false);
  };

  useEffect(
    () => () => {
      clearHide();
      clearAim();
    },
    []
  );

  // Track the cursor globally while open (panel is Portal'd off the rail).
  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      const locs = mouseLocs.current;
      locs.push({ x: e.clientX, y: e.clientY });
      if (locs.length > MOUSE_TRACK) locs.shift();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (hideTimer.current) {
          window.clearTimeout(hideTimer.current);
          hideTimer.current = null;
        }
        if (aimTimer.current) {
          window.clearTimeout(aimTimer.current);
          aimTimer.current = null;
        }
        setPinned(false);
        pinnedRef.current = false;
        setOpen(false);
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Position the panel next to the trigger (fixed coords — Portal is on body).
  // View As sits at the sidebar BOTTOM, so the top clamp usually pushes the
  // panel upward to fit; the notch tracks back to the trigger row.
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const r = trigger.getBoundingClientRect();
      const h = panel.offsetHeight;
      const GAP = 10;
      let top = r.top - 8;
      const maxTop = window.innerHeight - h - 12;
      if (top > maxTop) top = maxTop;
      if (top < 12) top = 12;
      let notch = r.top - top + r.height / 2 - 5.5;
      if (notch < 12) notch = 12;
      if (notch > h - 20) notch = h - 20;
      setPos({ top, left: Math.round(r.right + GAP), notch: Math.round(notch), opacity: 1 });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="view-as-flyout-trigger"
        onMouseEnter={openNow}
        onMouseLeave={scheduleHide}
        onFocus={openNow}
        onClick={togglePin}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`w-full flex items-center gap-2 bg-black/[0.03] border text-glass token-calendar rounded-lg py-1.5 pl-2 pr-2 font-medium cursor-pointer transition-colors ${
          open ? "border-blue-500 bg-black/[0.06]" : "border-black/[0.08] hover:bg-black/[0.06]"
        }`}
      >
        <span className="truncate flex-1 text-left">{triggerLabel}</span>
        <span className="flex-shrink-0 text-glass-muted opacity-70">
          <Icons.ChevronRight />
        </span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label="View as team member"
            onMouseEnter={() => {
              clearHide();
              clearAim();
            }}
            onMouseLeave={scheduleHide}
            style={
              {
                position: "fixed",
                top: pos.top,
                left: pos.left,
                opacity: pos.opacity,
                zIndex: 9990,
                "--notch": `${pos.notch}px`,
              } as React.CSSProperties
            }
            className="sidebar-flyout detail-overlay border border-black/[0.1] rounded-xl shadow-xl w-60 p-2 transition-opacity duration-150"
          >
            <div className="px-2 pt-1 pb-1.5">
              <span className="token-9 uppercase text-glass-muted tracking-wide">View as</span>
            </div>
            <div className="space-y-0.5 max-h-[60vh] overflow-y-auto">
              {items.map((item) => {
                const isCurrent = item.id === currentId;
                return (
                  <button
                    key={item.id || "__self__"}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isCurrent}
                    onClick={() => {
                      onSelect(item.id);
                      closeAll();
                    }}
                    className={`token-calendar w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 ${
                      isCurrent
                        ? "text-blue-400 bg-blue-500/10"
                        : "text-glass-secondary hover:bg-black/[0.04] hover:text-glass"
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 w-4 ${isCurrent ? "text-blue-400" : "opacity-0"}`}
                      aria-hidden="true"
                    >
                      <Icons.Check />
                    </span>
                    <span className="truncate flex-1 text-left font-medium">{item.label}</span>
                    {item.sublabel && (
                      <span className="token-9 text-glass-muted tracking-wide flex-shrink-0">
                        {item.sublabel}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Role Order for View-As Sorting
// ─────────────────────────────────────────────────────────────────────────────

const ROLE_ORDER: Record<string, number> = {
  exec: 0,
  admin: 0,
  client: 1,
  manager: 2,
  closer: 3,
  setter: 4,
  dialer: 5,
  viewer: 6,
};

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar Component
// ─────────────────────────────────────────────────────────────────────────────

export const SIDEBAR_COLLAPSED_KEY = "altara_sidebar_collapsed";

const Sidebar: React.FC = () => {
  const { user, realUser, logout, viewAsUser, setViewAsUser } = useAuth();
  const { currentOrganization, availableOrganizations, switchOrganization, teamMembers } =
    useData();
  const { role, isRealExec, hasTunnelVision } = useRBAC();
  const location = useLocation();
  // Theme is always light — no toggle needed
  // useGlassStyle() removed — now hoisted to AppLayout so it runs on ALL routes
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Desktop (lg+) gets the hover-out category flyout; mobile/narrow keeps the
  // inline accordion (hover doesn't exist on touch). Matches Tailwind's lg bp.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Realtime presence — track who's online, SCOPED PER ORG.
  // A user publishes to their current org and subscribes to every org they may
  // see: super-admins (god tier) see all orgs; everyone else sees only their
  // membership orgs. This mirrors get_user_orgs() — the RLS rule that already
  // gates all data access — so presence visibility never exceeds data
  // visibility. See recon 2026-07-09.
  const isSuperAdmin = !!realUser?.isSuperAdmin;
  const presenceVisibleOrgIds = isSuperAdmin
    ? availableOrganizations.map((o) => o.id)
    : Object.keys(realUser?.orgRoles || {});
  // Label each person by org only when the view spans more than one org.
  const presenceShowOrg = presenceVisibleOrgIds.length > 1;
  const { onlineUsers, status: presenceStatus } = usePresence({
    userId: realUser?.id || "",
    name: realUser?.name || "Unknown",
    role: realUser?.role || "viewer",
    homeOrgId: currentOrganization?.id ?? null,
    homeOrgName: currentOrganization?.name ?? null,
    visibleOrgIds: presenceVisibleOrgIds,
    // Option C: flip to true once the realtime RLS migration is live
    // (sql/migrations/2026-07-09_presence_channel_authorization.sql).
    usePrivateChannels: false,
    enabled: !!realUser?.id && !!currentOrganization?.id,
  });

  // Dev mode — double-click Altara logo to toggle. Hides WIP/dev pages from Nathaniel's view.
  const [devMode, setDevMode] = useState(() => {
    return localStorage.getItem("altara_dev_mode") === "true";
  });
  const handleLogoDoubleClick = () => {
    setDevMode((prev) => {
      const next = !prev;
      localStorage.setItem("altara_dev_mode", String(next));
      return next;
    });
  };

  // Collapsed state - synced with localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  });

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newState));
    // Dispatch storage event so App.tsx can react
    window.dispatchEvent(new Event("storage"));
  };

  const closeMobile = () => setIsMobileOpen(false);

  // ── Search state ──────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searching = searchQuery.trim().length > 0;
  // Keyboard-driven result selection (command-palette style: arrows move the
  // highlight, Enter jumps — focus never leaves the input, so no Tab needed).
  const [activeIndex, setActiveIndex] = useState(0);
  const searchListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // "/" focuses search from anywhere (unless already typing); Esc clears + blurs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const typing =
        !!el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT" ||
          el.isContentEditable);
      if (e.key === "/" && !typing) {
        if (!searchInputRef.current) return; // no search box (collapsed/viewer) → let "/" through
        e.preventDefault();
        searchInputRef.current.focus();
      } else if (e.key === "Escape" && el === searchInputRef.current) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Effective role for config lookups (defaults to 'viewer')
  const effectiveRole: UserRole = (role as UserRole) || "viewer";

  // Config-driven: which sidebar sections and pages this role sees
  const view = roleConfig[effectiveRole];
  const hasSection = (s: string) => view.sidebar.includes(s as any);
  // user = the effective (View As) user, so a temp dialer grant follows View As.
  const hasPage = (path: string) => canAccessPageForUser(effectiveRole, path, user?.id);

  // Layout: reps/client get a flat list, manager/exec get sectioned categories.
  const isClient = effectiveRole === "client";
  const showSections = !hasTunnelVision && !isClient;

  // Rep portal label: reps see "[Name]'s Portal", others see "Rep Portal".
  const repPortalLabel = hasTunnelVision
    ? `${(viewAsUser?.name || realUser?.name || "").split(" ")[0]}'s Portal`
    : "Rep Portal";

  // Display label for a path (3 runtime overrides stay caller-side per sidebarItems.ts).
  const labelFor = (path: string): string => {
    const meta = SIDEBAR_ITEMS[path];
    if (!meta) return path;
    if (path === "/admin/rep-portal") return repPortalLabel;
    if (path === "/admin/portal" && !showSections) return "Portal";
    return meta.label;
  };

  // ── RBAC + devMode filtered visible paths (SSOT for BOTH nav render AND search) ──
  const visiblePaths = useMemo(() => {
    const source = showSections
      ? [
          ...SECTIONED_LAYOUT.dashboards,
          ...SECTIONED_LAYOUT.management,
          ...SECTIONED_LAYOUT.devTools,
        ]
      : FLAT_LAYOUT_ORDER;
    return source.filter((path) => {
      if (!hasPage(path)) return false;
      const meta = SIDEBAR_ITEMS[path];
      if (!meta) return false;
      if (meta.devOnly && !devMode) return false; // dev pages hidden unless devMode
      if (meta.ownerOnly && !isOwner(realUser?.email)) return false; // Mako only
      // Portal placement: flat hides it when role has execTools; sectioned shows
      // it only when role has execTools (mirrors the historical renderItem rule).
      if (path === "/admin/portal" && !showSections && hasSection("execTools")) return false;
      if (path === "/admin/portal" && showSections && !hasSection("execTools")) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSections, devMode, effectiveRole, realUser?.email]);

  // Group visible paths by category, preserving source order within each.
  const itemsByCategory = useMemo(() => {
    const map = new Map<CategoryKey, string[]>();
    for (const path of visiblePaths) {
      const cat = SIDEBAR_ITEMS[path]?.category;
      if (!cat) continue;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(path);
    }
    return map;
  }, [visiblePaths]);

  // Categories that have ≥1 visible item, in taxonomy order — drives BOTH the
  // exec/manager flyout rail AND the mobile accordion (empty categories omit).
  const visibleCategories = useMemo<FlyoutCategory[]>(() => {
    return CATEGORY_ORDER.map((cat) => ({
      cat,
      paths: itemsByCategory.get(cat) ?? [],
      meta: CATEGORY_META[cat],
    })).filter((c) => c.paths.length > 0);
  }, [itemsByCategory]);

  // Search results — derived from the SAME RBAC-filtered set (RBAC inherited).
  // Each page's category label is injected as a search target, so typing a
  // section name ("Deals", "Analytics", "Comms") surfaces EVERY page nested
  // under it. Lets the user expand a whole group, not just match one page.
  const searchResults = useMemo(() => {
    if (!searching) return [];
    const items = visiblePaths.map((p) => {
      const meta = SIDEBAR_ITEMS[p];
      const catLabel = CATEGORY_META[meta.category]?.label;
      return catLabel ? { ...meta, aliases: [...meta.aliases, catLabel] } : meta;
    });
    return searchNav(searchQuery, items);
  }, [searching, searchQuery, visiblePaths]);

  // Every new query pre-selects the top match, so Enter jumps immediately.
  useEffect(() => {
    setActiveIndex(0);
  }, [searchQuery]);

  // Keep the highlighted result scrolled into view as arrows walk a long list.
  useEffect(() => {
    if (!searching || searchResults.length === 0) return;
    const el = searchListRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, searching, searchResults.length]);

  // Arrow keys move the highlight; Enter navigates to it and clears the search.
  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searching || searchResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % searchResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + searchResults.length) % searchResults.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = searchResults[Math.min(activeIndex, searchResults.length - 1)];
      if (!target) return;
      navigate(target.item.path);
      setSearchQuery("");
      searchInputRef.current?.blur();
      closeMobile();
    }
  };

  // Which category holds the current route → auto-expanded in the accordion.
  const activeCategory: CategoryKey | undefined = useMemo(() => {
    const match = visiblePaths.find(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
    return match ? SIDEBAR_ITEMS[match]?.category : undefined;
  }, [visiblePaths, location.pathname]);

  // Expanded categories (accordion). Active category auto-expands.
  const [expandedCats, setExpandedCats] = useState<Set<CategoryKey>>(() => new Set());
  useEffect(() => {
    if (!activeCategory) return;
    setExpandedCats((prev) =>
      prev.has(activeCategory) ? prev : new Set(prev).add(activeCategory)
    );
  }, [activeCategory]);
  const toggleCat = (k: CategoryKey) =>
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  // Build sorted list of team members for the "View as" dropdown
  const viewAsOptions = useMemo(() => {
    if (!isRealExec || !teamMembers.length) return [];
    return [...teamMembers]
      .filter((m) => m.status === "active")
      .sort((a, b) => {
        const ra = ROLE_ORDER[normalizeRole(a.role)] ?? 99;
        const rb = ROLE_ORDER[normalizeRole(b.role)] ?? 99;
        if (ra !== rb) return ra - rb;
        return (a.user_name || "").localeCompare(b.user_name || "");
      });
  }, [teamMembers, isRealExec]);

  const handleViewAs = async (userId: string) => {
    if (!userId) {
      setViewAsUser(null);
      return;
    }
    const member = teamMembers.find((m) => m.user_id === userId);
    if (!member) return;
    const memberRole = normalizeRole(member.role);
    // Pull the target's display currency so "View As" renders in their POV
    // (Mako 2026-07-16: viewing as Rayston should show SGD, not the exec's AUD).
    // organization_members_readable has no currency column, so read profiles
    // directly — one PK lookup, exec-only, fires only on a View-As switch.
    let displayCurrency: string | null = null;
    if (supabase) {
      const { data } = await supabase
        .from("profiles")
        .select("display_currency")
        .eq("id", member.user_id)
        .single();
      displayCurrency = (data?.display_currency as string | null) ?? null;
    }
    const override: ViewAsUser = {
      id: member.user_id,
      name: member.user_name || member.user_email,
      email: member.user_email,
      role: memberRole,
      sector: deriveSectorFromRole(memberRole),
      podId: member.pod_id || undefined,
      ghlUserId: member.ghl_user_id || undefined,
      displayCurrency,
    };
    setViewAsUser(override);
  };

  const roleLabel = (raw: string) => {
    const n = normalizeRole(raw);
    const labels: Record<string, string> = {
      exec: "Exec",
      client: "Client",
      manager: "Manager",
      closer: "Closer",
      setter: "Setter",
      dialer: "Dialer",
      viewer: "Viewer",
    };
    return labels[n] || raw;
  };

  // View-As flyout data — "Yourself" reset row + each active member (name + role
  // sublabel). Mirrors the native <select> options kept below as the test seam.
  const selfFirstName = realUser?.name?.split(" ")[0] || "Me";
  const viewAsItems: ViewAsItem[] = [
    { id: "", label: `Yourself (${selfFirstName})` },
    ...viewAsOptions.map((m) => ({
      id: m.user_id,
      label: m.user_name || m.user_email,
      sublabel: roleLabel(m.role),
    })),
  ];
  const viewAsTriggerLabel = viewAsUser ? viewAsUser.name : `Yourself (${selfFirstName})`;

  // Render a single nav item from a path.
  const renderNavItem = (
    path: string,
    opts?: { indented?: boolean; tag?: string; highlighted?: boolean }
  ) => {
    const meta = SIDEBAR_ITEMS[path];
    if (!meta) return null;
    const Icon = Icons[meta.iconKey];
    return (
      <NavItem
        key={path}
        to={path}
        icon={<Icon />}
        label={labelFor(path)}
        onClick={closeMobile}
        collapsed={isCollapsed}
        indented={opts?.indented}
        tag={opts?.tag}
        highlighted={opts?.highlighted}
      />
    );
  };

  return (
    <>
      {/* Mobile Header */}
      <div
        className="lg:hidden liquid-glass border-b border-black/[0.08] flex items-center px-4 justify-between"
        style={{ position: "fixed", top: 0, left: 0, right: 0, height: 56, zIndex: 40 }}
      >
        <span className="text-xl font-bold text-blue-400">Altara</span>
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="p-2 text-glass-secondary hover:text-glass hover:bg-black/[0.06] anim-tap rounded-lg transition-colors"
          aria-label={isMobileOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          <Icons.Menu />
        </button>
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`liquid-glass border-r border-black/[0.08] shadow-sm transform transition-all duration-200 ease-in-out flex flex-col flex-shrink-0 ${
          isCollapsed ? "w-14" : "w-48"
        } fixed inset-y-0 left-0 z-50 lg:sticky lg:top-0 lg:h-screen lg:z-auto ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        /* .liquid-glass is unlayered CSS and beats Tailwind's layered `fixed`/`lg:sticky`
           (position stayed `relative` below lg, keeping the off-canvas drawer IN flow and
           reserving ~192px on every page). Inline style wins the cascade — same pattern as
           the mobile header above. Desktop must stay class-driven: sticky is the contract. */
        style={{ position: isDesktop ? undefined : "fixed" }}
      >
        {/* Logo + Collapse Toggle */}
        <div className="h-12 flex items-center justify-between px-3 border-b border-black/[0.08] flex-shrink-0">
          {!isCollapsed && (
            <span
              className="text-lg font-bold text-blue-400 tracking-tight select-none cursor-default"
              onDoubleClick={handleLogoDoubleClick}
            >
              Altara
            </span>
          )}
          <button
            onClick={toggleCollapse}
            className={`p-1.5 text-glass-muted hover:text-glass hover:bg-black/[0.06] anim-tap rounded-md transition-colors ${isCollapsed ? "mx-auto" : ""}`}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <Icons.ChevronRight /> : <Icons.ChevronLeft />}
          </button>
        </div>

        {/* Organization Selector — only shown when the user can actually switch
            orgs (member of >1). Single-org users (most reps) default to their
            assigned org with no picker; multi-org members (e.g. a manager over
            several client orgs) and super-admins / exec — who see every org via
            the availableOrganizations filter in DataContext — get the switcher.
            Gods (Mako + Nathaniel = exec) always have all orgs, so they always
            see it. Mako, Jul 13 2026. */}
        {!isCollapsed && availableOrganizations.length > 1 && (
          <div className="p-2 border-b border-black/[0.08] flex-shrink-0">
            <label className="token-10 uppercase text-glass-muted mb-1 block px-1">
              Organization
            </label>
            {/* Desktop = side flyout on hover + click (/triangle menu-aim). Touch
                has no hover, so it keeps the native <select> below. */}
            {isDesktop ? (
              <>
                <OrgFlyout
                  organizations={availableOrganizations.map((o) => ({ id: o.id, name: o.name }))}
                  currentId={currentOrganization?.id ?? null}
                  currentName={currentOrganization?.name ?? null}
                  onSelect={switchOrganization}
                />
                {/* Native <select> kept mounted as the ACCESSIBLE + programmatic/
                    test control. Human-invisible (opacity 0, 1px box, no pointer
                    events) but still Playwright-actionable — .selectOption() only
                    rejects display:none / visibility:hidden / zero-box, so 100+
                    specs that switch org via this testid keep working. Both it and
                    the flyout are driven by currentOrganization → never desync. */}
                <select
                  data-testid="org-selector"
                  aria-hidden="true"
                  tabIndex={-1}
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                  }}
                  value={currentOrganization?.id || ""}
                  onChange={(e) => switchOrganization(e.target.value)}
                >
                  {availableOrganizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                  {availableOrganizations.length === 0 && <option disabled>No Orgs Found</option>}
                </select>
              </>
            ) : (
              <div className="relative">
                <select
                  data-testid="org-selector"
                  className="w-full bg-black/[0.03] border border-black/[0.08] text-glass token-calendar rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 py-1.5 pl-2 pr-6 appearance-none font-medium cursor-pointer transition-colors hover:bg-black/[0.06]"
                  value={currentOrganization?.id || ""}
                  onChange={(e) => switchOrganization(e.target.value)}
                >
                  {availableOrganizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                  {availableOrganizations.length === 0 && <option disabled>No Orgs Found</option>}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 pointer-events-none text-glass-muted">
                  <Icons.ChevronDown />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search — RBAC-gated synonym search (all roles). Hidden when collapsed. */}
        {!isCollapsed && effectiveRole !== "viewer" && (
          <div className="px-2 pt-2 pb-1 flex-shrink-0">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-glass-muted">
                <Icons.Search />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Search or jump to…"
                aria-label="Search navigation"
                role="combobox"
                aria-expanded={searching}
                aria-controls="nav-search-results"
                aria-autocomplete="list"
                data-testid="nav-search"
                className="w-full bg-black/[0.03] border border-black/[0.08] text-glass token-calendar rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 py-1.5 pl-7 pr-7 placeholder-black/30 transition-colors hover:bg-black/[0.06]"
              />
              {searching ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-2 text-glass-muted hover:text-glass"
                  aria-label="Clear search"
                >
                  <Icons.Close />
                </button>
              ) : (
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <kbd className="token-9 text-glass-muted border border-black/[0.08] rounded px-1 py-0.5 bg-black/[0.02]">
                    /
                  </kbd>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-1 px-1.5">
          {/* ── SEARCH RESULTS (any role, overrides normal nav while typing) ── */}
          {searching && !isCollapsed && (
            <div
              ref={searchListRef}
              id="nav-search-results"
              role="listbox"
              aria-label="Search results"
              className="space-y-0.5 pt-1"
            >
              {searchResults.length === 0 ? (
                <div className="px-2 py-6 text-center">
                  <p className="token-10 text-glass-muted leading-relaxed">
                    No matches for
                    <br />“{searchQuery.trim()}”
                  </p>
                </div>
              ) : (
                searchResults.map(({ item }, idx) =>
                  renderNavItem(item.path, {
                    tag: CATEGORY_META[item.category]?.label,
                    highlighted: idx === activeIndex,
                  })
                )
              )}
            </div>
          )}

          {/* ── COLLAPSED RAIL: flat icon-only list (no headers, no search) ── */}
          {isCollapsed && (
            <div className="space-y-0.5 pt-1">
              {visiblePaths.map((path) => renderNavItem(path))}
            </div>
          )}

          {/* ── FLAT SUB-GROUPED (reps + client), when not searching ── */}
          {!searching && !isCollapsed && !showSections && (
            <div className="pt-1">
              {CATEGORY_ORDER.map((cat) => {
                const paths = itemsByCategory.get(cat);
                if (!paths || paths.length === 0) return null;
                return (
                  <div key={cat}>
                    <SubGroupLabel>{CATEGORY_META[cat].label}</SubGroupLabel>
                    <div className="space-y-0.5">{paths.map((path) => renderNavItem(path))}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── SECTIONED CATEGORY NAV (exec + manager), when not searching ──
              Desktop = hover-out flyout rail (Mako, Jul 9 — replaced the
              rejected inline accordion). Mobile/narrow = inline accordion,
              since hover doesn't exist on touch. ── */}
          {!searching && !isCollapsed && showSections && isDesktop && (
            <ExecCategoryNav
              categories={visibleCategories}
              activeCategory={activeCategory}
              renderNavItem={renderNavItem}
            />
          )}
          {!searching && !isCollapsed && showSections && !isDesktop && (
            <div className="pt-1 space-y-0.5">
              {visibleCategories.map(({ cat, paths, meta }) => {
                const Icon = Icons[meta.iconKey];
                const open = expandedCats.has(cat);
                return (
                  <div key={cat}>
                    <CategoryHeader
                      label={meta.label}
                      icon={<Icon />}
                      count={paths.length}
                      open={open}
                      onToggle={() => toggleCat(cat)}
                    />
                    {open && (
                      <div className="space-y-0.5 mt-0.5 mb-1">
                        {paths.map((path) => renderNavItem(path, { indented: true }))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Presence widget — who's online, scoped per org. Visible to
              every role in both layouts, expanded only, when not searching. ── */}
          {!searching && (
            <>
              {!isCollapsed && (
                <div className="mt-3 px-2 border-t border-black/[0.08] pt-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {presenceStatus === "connected" ? (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                      </span>
                    ) : presenceStatus === "error" ? (
                      <span className="inline-flex rounded-full h-2 w-2 bg-red-400" />
                    ) : (
                      <span className="inline-flex rounded-full h-2 w-2 bg-amber-400 animate-pulse" />
                    )}
                    <span className="token-9 uppercase text-glass-muted tracking-wider">
                      {presenceStatus === "connected"
                        ? `Online · ${onlineUsers.length}`
                        : presenceStatus === "error"
                          ? "Presence error"
                          : "Connecting…"}
                    </span>
                  </div>
                  {onlineUsers.length > 0 && (
                    <div className="space-y-0.5">
                      {onlineUsers.map((u) => {
                        const pageName =
                          u.page === "/"
                            ? "Dashboard"
                            : u.page
                                .split("/")
                                .pop()
                                ?.replace(/-/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase()) || "—";
                        return (
                          <div
                            key={u.userId}
                            className="flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-black/[0.03] transition-colors"
                            title={`${u.name} (${u.role}) — ${pageName}${
                              presenceShowOrg && u.orgName ? ` · ${u.orgName}` : ""
                            }`}
                          >
                            <div
                              className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold shrink-0"
                              style={{ fontSize: "9px" }}
                            >
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="token-10 text-glass block truncate">{u.name}</span>
                              {presenceShowOrg && (
                                <span className="token-9 text-glass-muted block truncate">
                                  {u.orgName || "—"}
                                </span>
                              )}
                            </div>
                            <span className="token-9 text-glass-muted truncate max-w-[80px]">
                              {pageName}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </nav>

        {/* View As Section */}
        {isRealExec && viewAsOptions.length > 0 && !isCollapsed && (
          <div className="px-2 py-2 border-t border-black/[0.08] flex-shrink-0">
            <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
              <Icons.Eye />
              <span className="token-10 uppercase text-glass-muted">View as</span>
            </div>
            {/* Desktop = hover-out flyout (/triangle menu-aim, mirrors OrgFlyout).
                Touch has no hover, so it keeps the native <select> below. */}
            {isDesktop ? (
              <>
                <ViewAsFlyout
                  items={viewAsItems}
                  currentId={viewAsUser?.id || ""}
                  triggerLabel={viewAsTriggerLabel}
                  onSelect={handleViewAs}
                />
                {/* Native <select> kept MOUNTED but human-invisible (opacity 0,
                    1px box, no pointer events) as the a11y + test seam. View As
                    specs drive it via `aside select` .nth(1), the "View as"
                    container's <select>, and option[value=uuid] (afk-rls-carveout,
                    instagram-dms-migration, exec-role, sidebar-refactor-baseline).
                    Same seam pattern as the org-selector. Both it and the flyout
                    read `viewAsUser` → never desync. */}
                <select
                  data-testid="view-as-select"
                  aria-hidden="true"
                  tabIndex={-1}
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                  }}
                  value={viewAsUser?.id || ""}
                  onChange={(e) => handleViewAs(e.target.value)}
                >
                  <option value="">Yourself ({realUser?.name?.split(" ")[0]})</option>
                  {viewAsOptions.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.user_name || m.user_email} — {roleLabel(m.role)}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <div className="relative">
                <select
                  data-testid="view-as-select"
                  value={viewAsUser?.id || ""}
                  onChange={(e) => handleViewAs(e.target.value)}
                  className="w-full bg-black/[0.03] border border-black/[0.08] text-glass token-calendar rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 py-1.5 pl-2 pr-6 appearance-none font-medium cursor-pointer transition-colors hover:bg-black/[0.06]"
                >
                  <option value="">Yourself ({realUser?.name?.split(" ")[0]})</option>
                  {viewAsOptions.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.user_name || m.user_email} — {roleLabel(m.role)}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 pointer-events-none text-glass-muted">
                  <Icons.ChevronDown />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Theme toggle removed — Altara is light-mode-only */}

        {/* Display-currency toggle (USD ⇄ AUD) — self-gated to Mako + Nathaniel
            via canToggleCurrency; renders null for everyone else. */}
        <CurrencyToggle collapsed={isCollapsed} />

        {/* User Profile Section */}
        <div
          className={`border-t border-black/[0.08] flex-shrink-0 ${isCollapsed ? "p-2" : "p-3"}`}
        >
          <div className={`flex items-center ${isCollapsed ? "flex-col gap-2" : "gap-3"}`}>
            <div
              className={`rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold shadow-sm ${isCollapsed ? "w-8 h-8 token-10" : "w-9 h-9 token-11"}`}
              title={isCollapsed ? realUser?.name || "Guest" : undefined}
            >
              {realUser?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="token-11 font-medium text-glass truncate">
                  {realUser?.name || "Guest"}
                </p>
                <p className="token-9 text-glass-muted truncate capitalize">
                  {roleLabel(realUser?.role || "viewer")}
                </p>
              </div>
            )}
            <button
              onClick={logout}
              className={`text-glass-muted hover:text-red-400 hover:bg-red-500/10 anim-tap rounded-lg transition-all duration-150 ${isCollapsed ? "p-1.5" : "p-2"}`}
              title="Sign Out"
              aria-label="Sign out"
            >
              <Icons.Logout />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
